/**
 * @author Mukesh kumar bairwa <mukesh.bairwa@314ecorp.com>
 * @description Fields data
 */

import _ from 'lodash';
import { FieldOrGroup } from '@react-awesome-query-builder/antd';
import { OPERATORS } from './queryConfig';
import { MONTH_DATE_YEAR_SLASH_FORMAT, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';

export interface IFieldsData {
	field: string;
	display_name?: string;
	ui_field_type?: string;
	options?: AnyType[];
	treeData?: AnyType[];
	custom_props?: AnyType;
	searchOperator?: string;
	isSearchable?: boolean;
	order?: number;
	dependencyColumn?: string;
	operators?: string[];
	isDefaultSelectedField?: boolean;
	isAutoFocusedField?: boolean;
}

const DEFAULT_CUSTOM_PROPS = {
	size: 'large',
	style: {
		width: '100%',
	},
};

const QUERY_BUILDER_FILTER_TYPE = {
	date: 'date',
	datePicker: 'datePicker',
	rangePicker: 'rangePicker',
	multiselect: 'multiselect',
	select: 'select',
	text: 'text',
	number: 'number',
	textbox: 'textbox',
	treeSelect: 'treeselect',
	treeMultiSelect: 'treemultiselect',
	minMaxRange: 'minmaxrange',
};

const CORE_CONFIG = {
	[QUERY_BUILDER_FILTER_TYPE.date]: {
		widget: 'Date',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'date',
				label: definition.display_name,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
					valueFormat: definition.custom_props?.inputDateFormat || YEAR_MONTH_DATE_FORMAT,
				},
				defaultOperator: OPERATORS.equal,
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.datePicker]: {
		widget: 'DatePicker',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'date',
				label: definition.display_name,
				mainWidgetProps: {
					customProps: {
						format: MONTH_DATE_YEAR_SLASH_FORMAT,
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
					valueFormat: definition.custom_props?.inputDateFormat || YEAR_MONTH_DATE_FORMAT,
				},
				defaultOperator: OPERATORS.equal,
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.rangePicker]: {
		widget: 'RangePicker',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'date',
				label: definition.display_name,
				defaultOperator: OPERATORS.equal,
				mainWidgetProps: {
					customProps: {
						format: MONTH_DATE_YEAR_SLASH_FORMAT,
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.multiselect]: {
		widget: 'Select',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'multiselect',
				label: definition.display_name,
				fieldSettings: {
					listValues: _.map(definition.options, (option) => ({
						title: option.label,
						value: option.value,
					})),
				},
				defaultOperator: OPERATORS.multiselectContains,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
						maxTagCount: 1,
						allowClear: true,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.select]: {
		widget: 'Select',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'select',
				label: definition.display_name,
				fieldSettings: {
					listValues: _.map(definition.options, (option) => ({
						title: option.label,
						value: option.value,
					})),
				},
				defaultOperator: OPERATORS.equal,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.text]: {
		widget: 'Input',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'text',
				label: definition.display_name,
				defaultOperator: OPERATORS.like,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.textbox]: {
		widget: 'Input',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'text',
				defaultOperator: OPERATORS.like,
				label: definition.display_name,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.number]: {
		widget: 'InputNumber',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'number',
				label: definition.display_name,
				defaultOperator: OPERATORS.equal,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.treeSelect]: {
		widget: 'TreeSelect',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'treeselect',
				label: definition.display_name,
				fieldSettings: {
					treeValues: _.map(definition?.treeData, (option) => ({
						title: option.title,
						value: option.value,
						children: option?.children
							? _.map(option.children, (child) => ({
									title: child.title,
									value: child.value,
								}))
							: undefined,
					})),
				},
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},

	[QUERY_BUILDER_FILTER_TYPE.treeMultiSelect]: {
		widget: 'TreeSelect',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'treemultiselect',
				label: definition.display_name,
				fieldSettings: {
					treeValues: _.map(definition?.treeData, (option) => ({
						title: option.title,
						value: option.value,
						children: option?.children
							? _.map(option.children, (child) => ({
									title: child.title,
									value: child.value,
								}))
							: undefined,
					})),
				},
				mainWidgetProps: {
					customProps: {
						maxTagCount: 2,
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
	[QUERY_BUILDER_FILTER_TYPE.minMaxRange]: {
		widget: 'InputNumber',
		getField: (definition: IFieldsData): FieldOrGroup => {
			return {
				type: 'number',
				label: definition.display_name,
				defaultOperator: OPERATORS.equal,
				mainWidgetProps: {
					customProps: {
						...DEFAULT_CUSTOM_PROPS,
						...definition.custom_props,
					},
				},
			};
		},
	},
};

export { CORE_CONFIG, QUERY_BUILDER_FILTER_TYPE };
